import { Alert, Box, Snackbar } from '@mui/material';
import { Formik } from 'formik';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetPasswordFinish } from 'src/api/csApi';
import { LoginButton, LoginInput } from 'src/components/mui-overrides/Login';
import { ErrorMessage } from 'src/components/ui/ErrorMessage';
import { Colors } from 'src/components/utils/Theme';
import { Routes } from 'src/router/Routes';
import { authStore } from 'src/stores/AuthStore';
import { generalStore } from 'src/stores/GeneralStore';
import { object, ref, string } from 'yup';

export const AuthResetPasswordSite: FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('passwordToken');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate(Routes.ROOT);
    }
  }, []);

  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const { t } = useTranslation();
  const validationSchema = object({
    newPassword: string().required(t('resetPasswordDialog.newPasswordRequired')).min(8, t('resetPasswordDialog.newPasswordMinLength')),
    newPasswordConfirm: string()
      .oneOf([ref('newPassword')], t('resetPasswordDialog.passwordConfirmNotMatch'))
      .required(t('resetPasswordDialog.passwordConfirmRequired')),
  });
  const [showError, setShowError] = useState(false);

  const initialValues = {
    newPassword: '',
    newPasswordConfirm: '',
  };

  const handleSubmit = async (values: { newPassword: string; newPasswordConfirm: string }, actions: any) => {
    try {
      setShowError(false);
      generalStore.setIsLoading(true);
      await resetPasswordFinish(token ?? '', values.newPassword);
      if (authStore.error) {
        setShowError(true);
      } else {
        actions.resetForm();
        setShowSuccessAlert(true);
        navigate(Routes.ROOT);
      }
    } catch (error) {
      generalStore.setError('Unknown error');
    }

    generalStore.setIsLoading(false);
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={showSuccessAlert}
        autoHideDuration={6000}
        onClose={() => setShowSuccessAlert(false)}
      >
        <Alert onClose={() => setShowSuccessAlert(false)} severity="success" sx={{ width: '100%' }}>
          {t('resetPasswordDialog.success')}
        </Alert>
      </Snackbar>
      <div
        style={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: showError ? '5px solid ' + Colors.SECONDARY_COLOR : '',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flexGlow: 1,
            maxWidth: 350,
          }}
        >
          <img style={{ width: 170, marginBottom: 25 }} src="/logo.svg" alt="brickwise-logo" />

          <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {(formik) => (
              <form onSubmit={formik.handleSubmit}>
                <LoginInput
                  margin="normal"
                  fullWidth
                  id="newPassword"
                  label={t('resetPasswordDialog.newPassword.label')}
                  name="newPassword"
                  type="password"
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                  variant="filled"
                  helperText={formik.errors.newPassword && formik.touched.newPassword ? formik.errors.newPassword : ''}
                />
                <LoginInput
                  margin="normal"
                  fullWidth
                  id="newPasswordConfirm"
                  type="password"
                  label={t('resetPasswordDialog.newPasswordConfirm.label')}
                  name="newPasswordConfirm"
                  value={formik.values.newPasswordConfirm}
                  onChange={formik.handleChange}
                  error={formik.touched.newPasswordConfirm && Boolean(formik.errors.newPasswordConfirm)}
                  variant="filled"
                  helperText={formik.errors.newPasswordConfirm && formik.touched.newPasswordConfirm ? formik.errors.newPasswordConfirm : ''}
                />
                <LoginButton size="large" type="submit" fullWidth variant="contained" style={{ marginTop: 16 }}>
                  {t('resetPasswordDialog.button.label')}
                </LoginButton>
              </form>
            )}
          </Formik>
          <ErrorMessage
            style={{
              visibility: showError ? 'visible' : 'hidden',
            }}
            message={'Unknown error'}
          />
        </Box>
      </div>
    </>
  );
};
